import React, {
	FC, useEffect, useMemo, useState,
} from 'react';

import {
	ArrayElement, Checkbox, Counter, IColumnEditor, Label, Menu,
} from '@aleph/shared-components';
import { getRedditLocationOptions } from 'wb-reddit/app/routes/superGrid/targeting/utils';
import { useApolloClient } from '@apollo/client';
import { LocationOptionsQuery } from 'wb-reddit/graphql/generated';
import { Loader } from 'rsuite';

const LocationsColumnEditor: FC<IColumnEditor<LocationOptionsQuery['options']['items']>> = (params) => {
	const { value, setValue, editorContext } = params;
	const client = useApolloClient();

	const initialValues = useMemo(() => value, []);

	const [loading, setLoading] = useState<boolean>(true);
	const [options, setOptions] = useState<LocationOptionsQuery['options']['items']>([]);
	const [hasMore, setHasMore] = useState(false);

	const getDevices = async () => {
		setLoading(true);

		const { options: redditOptions, hasMore: hasMoreOptions } = await getRedditLocationOptions({
			client,
			search: editorContext?.search,
		});

		setLoading(false);
		setOptions(redditOptions);
		setHasMore(hasMoreOptions);
	};

	const toggleValue = (option: ArrayElement<LocationOptionsQuery['options']['items']>) => () => {
		if (value?.some(v => v?.value === option?.value)) {
			setValue(value?.filter(v => v?.value !== option?.value));

			return;
		}

		setValue([
			...(value ?? []),
			option,
		]);
	};

	useEffect(() => {
		if (editorContext) {
			getDevices();
		}
	}, [editorContext?.search ?? '']);

	const getLocationType = (location: ArrayElement<LocationOptionsQuery['options']['items']>) => {
		if (location?.postalCode) {
			return 'POSTAL CODE';
		}

		if (location?.dma) {
			return 'DMA';
		}

		if (location?.region) {
			return 'REGION';
		}

		return 'COUNTRY';
	};

	const renderItem = (option: ArrayElement<LocationOptionsQuery['options']['items']>) => (
		<Menu.Item
			onClick={toggleValue(option)}
			selected={value?.some(v => v?.value === option?.value)}
			alignItems="end"
		>
			<Label className="text-[14px] overflow-ellipsis">{option?.label}</Label>
			<Label className="text-gray-600 text-[10px] overflow-ellipsis">
				{getLocationType(option)}
			</Label>
			<Checkbox checked={value?.some(v => v?.value === option?.value)} />
		</Menu.Item>
	);

	return (
		<Menu
			className="flex flex-col min-h-full p-[8px]"
		>
			<>
				{loading
					&& <Loader
						vertical
						size="md"
						className="flex-1 !flex items-center justify-center"
						content="Loading locations"
					/>
				}

				{!loading
					&& <Menu.Item
						alignItems="space-between"
					>
						<Label className="text-gray-600 text-[12px]">
							LOCATIONS
						</Label>

						{
							(value?.length ?? 0) > 0
							&& <Counter
								className="relative"
								variant="square-accent"
								count={value?.length ?? 0}
							/>
						}
					</Menu.Item>
				}

				{!loading
					&& initialValues?.map(option => renderItem(option))
				}

				{!loading
					&& options?.filter(
						option => initialValues?.every(v => v?.value !== option?.value) ?? true,
					)?.map(option => renderItem(option))
				}

				{!loading
					&& hasMore
					&& <div className="flex items-center justify-center text-gray-700 py-[10px]">
						Search to find more locations
					</div>
				}
			</>
		</Menu>
	);
};

export default LocationsColumnEditor;
